import { useAppState } from '@/stores/AppState'
import axios, { AxiosError } from 'axios'
import type { AxiosInstance, AxiosResponse } from 'axios'
import HttpHelpers from './httpHelpers'
import { getAccessToken } from '../authentication'

export default class HttpClient {
  _axios: AxiosInstance
  store = useAppState()
  constructor(productCode: string) {
    let baseUrl: string = ''
    if (productCode !== undefined) {
      baseUrl =
        import.meta.env.VITE_RCRM_URL && productCode === 'RCRM'
          ? import.meta.env.VITE_RCRM_URL
          : this.store.$state.discovery?.endpoints.filter((ep) => ep.name === productCode)[0].url
      if (!baseUrl.endsWith('/')) {
        baseUrl += '/'
      }
      if (productCode === 'RCRM') {
        baseUrl += 'portalapi/'
      } else {
        baseUrl += 'api/'
      }
    }

    let softwareKey: string = ''
    const additionalInfo = this.store.$state.discovery?.endpoints.filter(
      (ep) => ep.name === productCode
    )[0].additionalInfo
    if (additionalInfo !== undefined) {
      softwareKey = additionalInfo.find((info) => info.key === 'SoftwareKey')?.value as string
    }
    this._axios = axios.create({
      baseURL: baseUrl,
      headers: {
        //'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE,PATCH,OPTIONS',
        SoftwareKey: softwareKey
      }
    })
  }

  get(path: string, queryStringObject?: any, config: any = {}) {
    path = HttpHelpers.objectToQueryString(path, queryStringObject)

    const resultPromise = new Promise<AxiosResponse<any>>((resolve, reject) => {
      getAccessToken()
        .then((accessToken) => {
          this._axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
          this._axios
            .get(path, config)
            .then((result) => {
              resolve(result)
            })
            .catch((e) => {
              if (e) {
                //Set flag so we know if its a server error or a network error.
                if (e.message == 'Network Error') {
                  e.isNetworkError = true
                } else if (e.response.data.errors) {
                  e.hasValidationErrors = true
                  e.validationMessages = e.response.data.errors
                }

                reject(e)
              } else {
                reject({ message: 'Unknown error' })
              }
            })
        })
        .catch((e) => {
          if (e) {
            //Set flag so we know if its a server error or a network error.
            if (e.message == 'Network Error') {
              e.isNetworkError = true
            } else if (e.response.data.errors) {
              e.hasValidationErrors = true
              e.validationMessages = e.response.data.errors
            }

            reject(e as CustomAxiosResponse)
          } else {
            reject({ message: 'Unknown error' })
          }
        })
    })

    return resultPromise
  }

  post(path: string, data: any, config: any = {}) {
    const resultPromise = new Promise<AxiosResponse<any>>((resolve, reject) => {
      getAccessToken()
        .then((accessToken) => {
          if (data) {
            data = HttpHelpers.updateDates(data)
          }

          this._axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
          return this._axios
            .post(path, data, config)
            .then((result) => {
              resolve(result)
            })
            .catch((e) => {
              if (e) {
                //Set flag so we know if its a server error or a network error.
                if (e.message == 'Network Error') {
                  e.isNetworkError = true
                } else if (e.response.data.errors) {
                  e.hasValidationErrors = true
                  e.validationMessages = e.response.data.errors
                }
                reject(e as CustomAxiosResponse)
              } else {
                reject({ message: 'Unknown error' })
              }
            })
        })
        .catch((e) => {
          if (e) {
            //Set flag so we know if its a server error or a network error.
            if (e.message == 'Network Error') {
              e.isNetworkError = true
            } else if (e.response.data.errors) {
              e.hasValidationErrors = true
              e.validationMessages = e.response.data.errors
            }

            reject(e as CustomAxiosResponse)
          } else {
            reject({ message: 'Unknown error' })
          }
        })
    })

    return resultPromise
  }

  postAnonymous(path: string, data: any) {
    const resultPromise = new Promise<AxiosResponse<any>>((resolve, reject) => {
      return this._axios
        .post(path, data)
        .then((result) => {
          resolve(result)
        })
        .catch((e) => {
          //Set flag so we know if its a server error or a network error.
          if (e.message == 'Network Error') {
            e.isNetworkError = true
          } else if (e.response.data.errors) {
            e.hasValidationErrors = true
            e.validationMessages = e.response.data.errors
          }

          reject(e as CustomAxiosResponse)
        })
    })

    return resultPromise
  }
}

export interface CustomAxiosResponse<T = any> extends AxiosError {
  isNetworkError: boolean
  hasValidationErrors: boolean
  validationErrors: any[]
}
